/**
 * Item Catalog v2 (Iter 240)
 *
 * Excel-like inline editing for every item column, CSV/paste import,
 * per-row edit history (with revert to prior rate), and curated Category
 * management dialog. Columns: SKU · Name · Category · Unit · Rate ·
 * Effort (h) · Tax % · HSN/SAC · Currency · Service? · Active?
 */
import { useEffect, useMemo, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Search, Trash2, Package, Save, X, History as HistoryIcon,
  ClipboardPaste, Copy, RotateCcw, Tag as TagIcon, Paperclip,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";
import AttachmentsPanel from "@/pages/app/gst-sales/AttachmentsPanel";

const UNIT_HINTS = ["unit", "hr", "day", "week", "month", "project", "license", "post", "reel", "doc", "asset", "user", "seat"];
const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AED"];

const emptyRow = () => ({
  id: null, sku: "", name: "", description: "", category: "",
  unit: "unit", rate: 0, effort_hours: "", tax_pct: 0, hsn_sac: "",
  currency: "INR", is_service: false, active: true, _dirty: false,
});

export default function ItemCatalog() {
  const { activeWorkspaceId } = useAuth();
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [categories, setCategories] = useState([]);
  const [showCsv, setShowCsv] = useState(searchParams.get("import") === "1");
  const [csvText, setCsvText] = useState("");
  const [analytics, setAnalytics] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(true);
  const [historyItem, setHistoryItem] = useState(null);
  const [specsItem, setSpecsItem] = useState(null);
  const [showCategories, setShowCategories] = useState(false);
  const gridRef = useRef(null);

  const buildRows = (data) => (data || []).map((it) => ({
    id: it.id, sku: it.sku || "", name: it.name || "",
    description: it.description || "", category: it.category || "",
    unit: it.unit || "unit", rate: it.rate ?? 0,
    effort_hours: it.effort_hours ?? "", tax_pct: it.tax_pct ?? 0,
    hsn_sac: it.hsn_sac || "", currency: it.currency || "INR",
    is_service: !!it.is_service, active: it.active !== false, _dirty: false,
  }));

  const load = async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (categoryFilter) params.category = categoryFilter;
      const [itemsRes, catRes, anRes] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/items`, { params }),
        api.get(`/workspaces/${activeWorkspaceId}/item-categories`).catch(() => ({ data: [] })),
        api.get(`/workspaces/${activeWorkspaceId}/items/analytics`).catch(() => ({ data: null })),
      ]);
      setRows(buildRows(itemsRes.data));
      setCategories(catRes.data || []);
      setAnalytics(anRes.data);
      setDirty(false);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load catalog"));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [activeWorkspaceId, categoryFilter]); // eslint-disable-line
  useEffect(() => {
    if (!activeWorkspaceId) return;
    const t = setTimeout(load, 220);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const inferredCats = useMemo(() => {
    const set = new Set(categories.map((c) => c.name));
    rows.forEach((r) => { if (r.category) set.add(r.category); });
    return Array.from(set).sort();
  }, [rows, categories]);

  const setCell = (idx, key, val) => {
    setRows((prev) => prev.map((r, i) => i === idx ? { ...r, [key]: val, _dirty: true } : r));
    setDirty(true);
  };

  const addRow = () => {
    setRows((prev) => [...prev, { ...emptyRow(), _dirty: true }]);
    setDirty(true);
    setTimeout(() => {
      const inputs = gridRef.current?.querySelectorAll('[data-testid^="item-cell-name-"]');
      if (inputs?.length) inputs[inputs.length - 1].focus();
    }, 30);
  };

  const removeRow = (idx) => {
    setRows((prev) => prev.map((r, i) => i === idx
      ? (r.id ? { ...r, _delete: true, _dirty: true } : null)
      : r).filter(Boolean));
    setDirty(true);
  };

  const duplicateRow = (idx) => {
    setRows((prev) => {
      const src = prev[idx];
      const clone = { ...src, id: null, sku: "",
                        name: (src.name || "") + " (copy)", _dirty: true };
      const next = [...prev];
      next.splice(idx + 1, 0, clone);
      return next;
    });
    setDirty(true);
  };

  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); addRow(); }
    if ((e.metaKey || e.ctrlKey) && e.key === "s") { e.preventDefault(); save(); }
  };

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      const payload = {
        rows: rows.map((r) => ({
          id: r.id || undefined,
          sku: r.sku || undefined,
          name: r.name || "(untitled)",
          description: r.description || undefined,
          category: r.category || undefined,
          unit: r.unit || "unit",
          rate: Number(r.rate) || 0,
          effort_hours: r.effort_hours === "" || r.effort_hours == null
                          ? null : Number(r.effort_hours),
          tax_pct: Number(r.tax_pct) || 0,
          hsn_sac: r.hsn_sac || undefined,
          currency: r.currency || "INR",
          is_service: !!r.is_service,
          active: !!r.active,
          delete: !!r._delete,
        })),
      };
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/items/bulk`, payload);
      toast.success(`Saved · ${data.created} added, ${data.updated} updated, ${data.deleted} removed`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const parseCsv = () => {
    const text = (csvText || "").trim();
    if (!text) return;
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (!lines.length) return;
    // Iter 241 — detect tab-delimited (Google Sheets copy-paste) automatically
    const useTabs = lines[0].includes("\t");
    const splitCell = (line) => (useTabs ? line.split("\t") : line.split(","))
                                   .map((c) => c.trim());
    // Detect header
    const firstLower = lines[0].toLowerCase();
    const firstCol = splitCell(lines[0])[0]?.toLowerCase() || "";
    const hasHeader = /name|item|sku|title|description/.test(firstCol);
    let header = ["name", "sku", "category", "unit", "rate", "effort_hours",
                   "tax_pct", "hsn_sac", "currency", "description"];
    let dataLines = lines;
    if (hasHeader) {
      header = splitCell(lines[0]).map((h) => h.toLowerCase().replace(/\s+/g, "_"));
      dataLines = lines.slice(1);
    }
    void firstLower;
    const parsed = dataLines.map((line) => {
      const cells = splitCell(line);
      const rec = emptyRow();
      rec._dirty = true;
      header.forEach((col, i) => {
        const v = cells[i] || "";
        if (col === "name" || col === "item" || col === "title") rec.name = v;
        else if (col === "sku") rec.sku = v;
        else if (col === "category") rec.category = v;
        else if (col === "unit") rec.unit = v || "unit";
        else if (col === "rate" || col === "price") rec.rate = Number(v) || 0;
        else if (col === "effort_hours" || col === "effort" || col === "hours") rec.effort_hours = v ? Number(v) : "";
        else if (col === "tax_pct" || col === "tax" || col === "gst") rec.tax_pct = Number(v) || 0;
        else if (col === "hsn_sac" || col === "hsn" || col === "sac") rec.hsn_sac = v;
        else if (col === "currency") rec.currency = v || "INR";
        else if (col === "description" || col === "desc") rec.description = v;
      });
      return rec;
    }).filter((r) => r.name);
    if (!parsed.length) { toast.error("No rows detected"); return; }
    setRows((prev) => [...prev, ...parsed]);
    setDirty(true);
    setShowCsv(false);
    setCsvText("");
    const src = useTabs ? "Google Sheets" : "CSV";
    toast.success(`Loaded ${parsed.length} row${parsed.length === 1 ? "" : "s"} from ${src} — click Save changes to persist`);
  };

  // Iter 241 · Grid-level paste handler for Google Sheets tab-delimited data
  const handleGridPaste = (e) => {
    const txt = e.clipboardData?.getData("text");
    if (!txt || !txt.includes("\t")) return;
    if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
    e.preventDefault();
    setCsvText(txt);
    setShowCsv(true);
    // Auto-parse after a tick so the panel is mounted for visual confirmation
    setTimeout(() => {
      // Trigger parseCsv via microtask so csvText is already set
      const lines = txt.split(/\r?\n/).filter((l) => l.trim());
      const parsed = lines.map((line) => {
        const cells = line.split("\t").map((c) => c.trim());
        const rec = emptyRow();
        rec._dirty = true;
        // Best-effort positional mapping when no header
        rec.name = cells[0] || "";
        if (cells[1]) rec.sku = cells[1];
        if (cells[2]) rec.category = cells[2];
        if (cells[3]) rec.unit = cells[3];
        if (cells[4]) rec.rate = Number(cells[4]) || 0;
        if (cells[5]) rec.effort_hours = Number(cells[5]) || "";
        if (cells[6]) rec.tax_pct = Number(cells[6]) || 0;
        if (cells[7]) rec.hsn_sac = cells[7];
        return rec;
      }).filter((r) => r.name);
      if (!parsed.length) return;
      // Detect and skip header row
      const startIdx = /name|item|sku/i.test(parsed[0].name) ? 1 : 0;
      const rowsToAdd = parsed.slice(startIdx);
      if (!rowsToAdd.length) return;
      setRows((prev) => [...prev, ...rowsToAdd]);
      setDirty(true);
      setShowCsv(false);
      setCsvText("");
      toast.success(`Pasted ${rowsToAdd.length} row${rowsToAdd.length === 1 ? "" : "s"} from Google Sheets — click Save changes`);
    }, 0);
  };

  const csym = (c) => c === "INR" ? "₹" : (c || "") + " ";
  const visible = rows.filter((r) => !r._delete);

  return (
    <>
      <PageHeader
        eyebrow="Sales & Estimates"
        title="Item catalog"
        description="Excel-like editor for every item. Type inline, ⌘⏎ adds a row, ⌘S saves. Paste from CSV to onboard a rate card. Categories are curated for analytics."
        testid="items-header"
        actions={activeWorkspaceId && (
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-none"
                     onClick={() => setShowCategories(true)}
                     data-testid="items-manage-categories">
              <TagIcon className="w-3.5 h-3.5 mr-1.5" /> Categories
            </Button>
            <Button variant="outline" className="rounded-none"
                     onClick={() => setShowCsv(true)}
                     data-testid="items-csv-btn">
              <ClipboardPaste className="w-3.5 h-3.5 mr-1.5" /> Paste CSV
            </Button>
            <Button className="rounded-none" onClick={addRow} data-testid="items-add-row">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add row
              <span className="text-[9px] opacity-70 ml-1.5">⌘⏎</span>
            </Button>
            <Button className="rounded-none" onClick={save} disabled={!dirty || saving}
                     data-testid="items-save">
              <Save className="w-3.5 h-3.5 mr-1.5" />
              {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
            </Button>
          </div>
        )}
      />

      <div className="px-4 sm:px-6 md:px-10 py-6 space-y-4">
        <CategoryAnalytics analytics={analytics}
                            currency="INR"
                            expanded={showAnalytics}
                            onToggle={() => setShowAnalytics((v) => !v)}
                            onCategoryClick={(name) => setCategoryFilter(name === categoryFilter ? "" : name)}
                            activeCategory={categoryFilter} />

        <div className="flex items-center gap-2 flex-wrap" data-testid="items-filters">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
                    placeholder="Search by name, SKU, HSN, description…"
                    className="pl-8 rounded-none h-9"
                    data-testid="items-search" />
          </div>
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
                  className="text-xs h-9 px-2 border border-border bg-background"
                  data-testid="items-category-filter">
            <option value="">All categories</option>
            {inferredCats.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <span className="text-xs text-muted-foreground ml-auto">
            {loading ? "Loading…" : `${visible.length} item${visible.length === 1 ? "" : "s"}${dirty ? " · unsaved changes" : ""}`}
          </span>
        </div>

        {showCsv && (
          <div className="border border-dashed border-border p-3 space-y-2 bg-muted/20" data-testid="items-csv-panel">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <Copy className="w-3 h-3" /> Paste CSV or Google Sheets (tab-delimited) — columns: name, sku, category, unit, rate, effort_hours, tax_pct, hsn_sac, currency, description
            </div>
            <Textarea rows={7}
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              placeholder={"Copy from Google Sheets — auto-detects tabs\nOR paste CSV like below:\nname,sku,category,unit,rate,effort_hours,tax_pct,hsn_sac\nLogo Redesign,DES-001,Design,job,25000,16,18,998314"}
              className="font-mono text-xs"
              data-testid="items-csv-textarea" />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => { setShowCsv(false); setCsvText(""); }}>Cancel</Button>
              <Button size="sm" onClick={parseCsv} data-testid="items-csv-load">Load rows into grid</Button>
            </div>
          </div>
        )}

        {visible.length === 0 && !loading && !dirty ? (
          <EmptyState
            title="No items yet"
            description="Add items inline (⌘⏎) or paste your existing rate card via CSV to bootstrap."
            icon={Package}
            testid="items-empty"
            action={
              <div className="flex gap-2">
                <Button onClick={addRow} className="rounded-none">Add first item</Button>
                <Button variant="outline" onClick={() => setShowCsv(true)} className="rounded-none">Paste CSV</Button>
              </div>
            }
          />
        ) : (
          <div className="border border-border overflow-x-auto" ref={gridRef} onKeyDown={onKeyDown} onPaste={handleGridPaste} data-testid="items-grid">
            <table className="w-full text-xs min-w-[1400px]">
              <thead className="bg-muted/40 border-b border-border sticky top-0 z-10">
                <tr className="text-left text-[10px] uppercase text-muted-foreground">
                  <th className="px-2 py-2 w-10">#</th>
                  <th className="px-2 py-2 w-32">SKU</th>
                  <th className="px-2 py-2">Name *</th>
                  <th className="px-2 py-2 w-36">Category</th>
                  <th className="px-2 py-2 w-24">Unit</th>
                  <th className="px-2 py-2 w-28 text-right">Rate</th>
                  <th className="px-2 py-2 w-20 text-right">Effort (h)</th>
                  <th className="px-2 py-2 w-16 text-right">Tax %</th>
                  <th className="px-2 py-2 w-28">HSN / SAC</th>
                  <th className="px-2 py-2 w-20">Currency</th>
                  <th className="px-2 py-2 w-14 text-center">Service</th>
                  <th className="px-2 py-2 w-14 text-center">Active</th>
                  <th className="px-2 py-2 w-24"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((r, i) => r._delete ? null : (
                  <tr key={r.id || `new-${i}`} data-testid={`item-row-${i}`}
                      className={r._dirty ? "bg-amber-50/40" : ""}>
                    <td className="px-2 py-1 text-muted-foreground align-top pt-2">{i + 1}</td>
                    <td className="px-1 py-1">
                      <input value={r.sku} onChange={(e) => setCell(i, "sku", e.target.value)}
                              className="w-full text-xs px-2 py-1 bg-background border border-border font-mono focus:border-primary focus:outline-none"
                              data-testid={`item-cell-sku-${i}`} />
                    </td>
                    <td className="px-1 py-1">
                      <input value={r.name} onChange={(e) => setCell(i, "name", e.target.value)}
                              placeholder="Line item name"
                              className="w-full text-xs px-2 py-1 bg-background border border-border focus:border-primary focus:outline-none"
                              data-testid={`item-cell-name-${i}`} />
                      {r.description && (
                        <input value={r.description} onChange={(e) => setCell(i, "description", e.target.value)}
                                placeholder="Description"
                                className="w-full text-[11px] mt-1 px-2 py-0.5 bg-background border border-border text-muted-foreground focus:border-primary focus:outline-none" />
                      )}
                    </td>
                    <td className="px-1 py-1">
                      <input value={r.category} onChange={(e) => setCell(i, "category", e.target.value)}
                              list="item-cat-list-grid"
                              placeholder="—"
                              className="w-full text-xs px-2 py-1 bg-background border border-border focus:border-primary focus:outline-none"
                              data-testid={`item-cell-category-${i}`} />
                    </td>
                    <td className="px-1 py-1">
                      <input value={r.unit} onChange={(e) => setCell(i, "unit", e.target.value)}
                              list="item-unit-list-grid"
                              className="w-full text-xs px-2 py-1 bg-background border border-border focus:border-primary focus:outline-none"
                              data-testid={`item-cell-unit-${i}`} />
                    </td>
                    <td className="px-1 py-1">
                      <input type="number" step="1" value={r.rate}
                              onChange={(e) => setCell(i, "rate", e.target.value)}
                              className="w-full text-xs px-2 py-1 bg-background border border-border tabular-nums text-right focus:border-primary focus:outline-none"
                              data-testid={`item-cell-rate-${i}`} />
                    </td>
                    <td className="px-1 py-1">
                      <input type="number" step="0.5" value={r.effort_hours}
                              onChange={(e) => setCell(i, "effort_hours", e.target.value)}
                              placeholder="—"
                              className="w-full text-xs px-2 py-1 bg-background border border-border tabular-nums text-right focus:border-primary focus:outline-none"
                              data-testid={`item-cell-effort-${i}`} />
                    </td>
                    <td className="px-1 py-1">
                      <input type="number" step="0.5" value={r.tax_pct}
                              onChange={(e) => setCell(i, "tax_pct", e.target.value)}
                              className="w-full text-xs px-2 py-1 bg-background border border-border tabular-nums text-right focus:border-primary focus:outline-none"
                              data-testid={`item-cell-tax-${i}`} />
                    </td>
                    <td className="px-1 py-1">
                      <input value={r.hsn_sac} onChange={(e) => setCell(i, "hsn_sac", e.target.value)}
                              placeholder="e.g. 998314"
                              className="w-full text-xs px-2 py-1 bg-background border border-border font-mono focus:border-primary focus:outline-none"
                              data-testid={`item-cell-hsn-${i}`} />
                    </td>
                    <td className="px-1 py-1">
                      <select value={r.currency} onChange={(e) => setCell(i, "currency", e.target.value)}
                               className="w-full text-xs px-1 py-1 bg-background border border-border"
                               data-testid={`item-cell-currency-${i}`}>
                        {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </td>
                    <td className="px-1 py-1 text-center">
                      <input type="checkbox" checked={r.is_service}
                              onChange={(e) => setCell(i, "is_service", e.target.checked)}
                              data-testid={`item-cell-service-${i}`} />
                    </td>
                    <td className="px-1 py-1 text-center">
                      <input type="checkbox" checked={r.active}
                              onChange={(e) => setCell(i, "active", e.target.checked)}
                              data-testid={`item-cell-active-${i}`} />
                    </td>
                    <td className="px-1 py-1 text-right">
                      <div className="inline-flex items-center gap-0.5">
                        {r.id && (
                          <button type="button" onClick={() => setSpecsItem(r)}
                                   className="p-1 text-muted-foreground hover:text-foreground relative"
                                   title="Spec sheet attachments"
                                   data-testid={`item-specs-${i}`}>
                            <Paperclip className="w-3 h-3" />
                            {Array.isArray(r.spec_attachments) && r.spec_attachments.length > 0 && (
                              <span className="absolute -top-1 -right-1 min-w-[12px] h-3 px-0.5 rounded-full bg-amber-500 text-[8px] font-bold text-white flex items-center justify-center" data-testid={`item-specs-count-${i}`}>
                                {r.spec_attachments.length}
                              </span>
                            )}
                          </button>
                        )}
                        {r.id && (
                          <button type="button" onClick={() => setHistoryItem(r)}
                                   className="p-1 text-muted-foreground hover:text-foreground"
                                   title="Edit history"
                                   data-testid={`item-history-${i}`}>
                            <HistoryIcon className="w-3 h-3" />
                          </button>
                        )}
                        <button type="button" onClick={() => duplicateRow(i)}
                                 className="p-1 text-muted-foreground hover:text-foreground"
                                 title="Duplicate row"
                                 data-testid={`item-duplicate-${i}`}>
                          <Copy className="w-3 h-3" />
                        </button>
                        <button type="button" onClick={() => removeRow(i)}
                                 className="p-1 text-muted-foreground hover:text-rose-500"
                                 title="Delete"
                                 data-testid={`item-delete-${i}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <datalist id="item-cat-list-grid">
              {inferredCats.map((c) => <option key={c} value={c} />)}
            </datalist>
            <datalist id="item-unit-list-grid">
              {UNIT_HINTS.map((u) => <option key={u} value={u} />)}
            </datalist>
          </div>
        )}
      </div>

      {historyItem && (
        <ItemHistoryDialog item={historyItem}
                            onClose={() => setHistoryItem(null)}
                            onReverted={load} />
      )}
      {/* Feb 2026 R293zv · Spec sheet attachments for a catalog item.
          Reuses the same AttachmentsPanel the sales Composer uses, but
          hits the item-scoped endpoints (`/items/{iid}/attachments`). */}
      {specsItem && (
        <ItemSpecsDialog item={specsItem}
                          onClose={() => { setSpecsItem(null); load(); }} />
      )}
      {showCategories && (
        <CategoriesDialog wsId={activeWorkspaceId}
                           onClose={() => setShowCategories(false)}
                           onChanged={load} />
      )}
    </>
  );
}


/* ────── History dialog with revert-rate ────── */
function ItemHistoryDialog({ item, onClose, onReverted }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancel = false;
    api.get(`/items/${item.id}/history`).then((r) => {
      if (!cancel) { setRows(r.data || []); setLoading(false); }
    }).catch(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [item.id]);

  const revertRate = async (fromRate) => {
    if (fromRate == null || Number.isNaN(Number(fromRate))) return;
    if (!window.confirm(`Revert rate to ${fromRate}?`)) return;
    try {
      await api.patch(`/items/${item.id}`, { rate: Number(fromRate) });
      toast.success("Rate reverted");
      onReverted();
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Revert failed")); }
  };

  const rateChanges = rows.filter((r) => r.changes && r.changes.rate);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="item-history-dialog">
        <DialogHeader>
          <DialogTitle>{item.name} · history</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="text-xs text-muted-foreground">Loading…</div>
        ) : (
          <div className="flex-1 overflow-auto space-y-4">
            {rateChanges.length > 0 && (
              <div className="border border-border">
                <div className="px-3 py-2 bg-muted/40 text-[10px] uppercase font-semibold">
                  Rate history · {rateChanges.length} change{rateChanges.length === 1 ? "" : "s"}
                </div>
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase text-muted-foreground">
                    <tr className="text-left">
                      <th className="px-2 py-1">When</th>
                      <th className="px-2 py-1">By</th>
                      <th className="px-2 py-1 text-right">From</th>
                      <th className="px-2 py-1 text-right">To</th>
                      <th className="px-2 py-1 w-24"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {rateChanges.map((r) => (
                      <tr key={r.id} data-testid={`item-history-rate-${r.id}`}>
                        <td className="px-2 py-1 text-muted-foreground">{fmtRelative(r.at)}</td>
                        <td className="px-2 py-1">{r.actor_name}</td>
                        <td className="px-2 py-1 text-right tabular-nums line-through text-rose-600">
                          {r.changes.rate.from ?? "—"}
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums text-emerald-700">
                          {r.changes.rate.to ?? "—"}
                        </td>
                        <td className="px-2 py-1 text-right">
                          <button type="button" onClick={() => revertRate(r.changes.rate.from)}
                                   className="text-[10px] px-2 py-0.5 border border-border hover:bg-muted inline-flex items-center gap-1"
                                   title="Revert to this prior rate"
                                   data-testid={`item-revert-${r.id}`}>
                            <RotateCcw className="w-3 h-3" /> Revert
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="border border-border">
              <div className="px-3 py-2 bg-muted/40 text-[10px] uppercase font-semibold">
                All changes · {rows.length}
              </div>
              {rows.length === 0 ? (
                <div className="p-4 text-xs text-muted-foreground italic">No history yet.</div>
              ) : (
                <div className="divide-y divide-border">
                  {rows.map((r) => (
                    <div key={r.id} className="p-2 text-xs" data-testid={`item-history-row-${r.id}`}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{r.action}</span>
                        <span className="text-muted-foreground">by <b>{r.actor_name}</b></span>
                        <span className="text-muted-foreground ml-auto">{fmtRelative(r.at)}</span>
                      </div>
                      {r.changes && Object.keys(r.changes).length > 0 && (
                        <div className="mt-1 pl-2 border-l-2 border-border space-y-0.5">
                          {Object.entries(r.changes).map(([k, v]) => (
                            <div key={k} className="text-[11px]">
                              <span className="font-mono text-muted-foreground">{k}</span>
                              <span> · <span className="line-through text-rose-600">{formatVal(v.from)}</span> → <span className="text-emerald-700">{formatVal(v.to)}</span></span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function formatVal(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}


/* ────── Categories management ────── */
function CategoriesDialog({ wsId, onClose, onChanged }) {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#3b82f6");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/item-categories`);
      setCats(data || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const add = async () => {
    if (!newName.trim()) return;
    try {
      await api.post(`/workspaces/${wsId}/item-categories`, {
        name: newName.trim(), color: newColor,
        sort_order: cats.length,
      });
      setNewName(""); load(); onChanged();
    } catch (e) { toast.error(apiErrorMessage(e, "Add failed")); }
  };
  const rename = async (c, name) => {
    if (!name.trim() || name === c.name) return;
    try {
      await api.patch(`/item-categories/${c.id}`, {
        name: name.trim(), color: c.color, icon: c.icon,
        sort_order: c.sort_order,
      });
      toast.success("Renamed (items updated)");
      load(); onChanged();
    } catch (e) { toast.error(apiErrorMessage(e, "Rename failed")); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"?\n(${c.usage_count || 0} items keep the category label but lose the setup entry.)`)) return;
    try {
      await api.delete(`/item-categories/${c.id}`);
      load(); onChanged();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="item-categories-dialog">
        <DialogHeader>
          <DialogTitle>Manage item categories</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-muted-foreground -mt-2">
          Curated categories power analytics later (mix by category, category
          rate trends). Renaming a category cascades to every item using it.
        </div>
        <div className="border border-border p-2 flex items-center gap-2">
          <input type="color" value={newColor}
                  onChange={(e) => setNewColor(e.target.value)}
                  className="w-8 h-8 border border-border cursor-pointer p-0.5"
                  data-testid="cat-new-color" />
          <input value={newName} onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") add(); }}
                  placeholder="New category (e.g. Design, Development, Retainer)"
                  className="flex-1 text-xs px-2 py-1.5 bg-background border border-border"
                  data-testid="cat-new-name" />
          <Button size="sm" onClick={add} disabled={!newName.trim()}
                   data-testid="cat-add">Add</Button>
        </div>
        <div className="max-h-72 overflow-auto border border-border divide-y divide-border">
          {loading && <div className="p-3 text-xs text-muted-foreground">Loading…</div>}
          {!loading && cats.length === 0 && (
            <div className="p-3 text-xs text-muted-foreground italic">No categories yet.</div>
          )}
          {cats.map((c) => (
            <div key={c.id} className="p-2 flex items-center gap-2" data-testid={`cat-row-${c.id}`}>
              <span className="inline-block w-3 h-3 border border-border"
                     style={{ background: c.color || "#94a3b8" }} />
              <input defaultValue={c.name}
                      onBlur={(e) => rename(c, e.target.value)}
                      className="flex-1 text-xs px-2 py-1 bg-background border border-transparent hover:border-border focus:border-primary focus:outline-none"
                      data-testid={`cat-name-${c.id}`} />
              <span className="text-[10px] text-muted-foreground w-16 text-right">{c.usage_count || 0} items</span>
              <button type="button" onClick={() => remove(c)}
                       className="p-1 text-muted-foreground hover:text-rose-500"
                       data-testid={`cat-delete-${c.id}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex justify-end">
          <Button variant="ghost" onClick={onClose}
                   className="rounded-none"
                   data-testid="cat-close">
            <X className="w-3.5 h-3.5 mr-1" /> Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}



// ─────────── Iter 241 · Category Analytics rollup strip ───────────

function fmtRel(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    if (diff < 86400 * 30) return `${Math.round(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  } catch { return "—"; }
}

function fmtCurrency(n, currency = "INR") {
  const val = Number(n || 0);
  const csym = currency === "INR" ? "₹" : currency + " ";
  if (val >= 1e7) return `${csym}${(val / 1e7).toFixed(2)}Cr`;
  if (val >= 1e5) return `${csym}${(val / 1e5).toFixed(2)}L`;
  if (val >= 1000) return `${csym}${(val / 1000).toFixed(1)}k`;
  return `${csym}${val.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function CategoryAnalytics({ analytics, currency = "INR", expanded, onToggle, onCategoryClick, activeCategory }) {
  if (!analytics) return null;
  return (
    <div className="border border-border" data-testid="items-analytics">
      <div className="px-3 py-2 border-b border-border bg-muted/30 flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
          Catalog analytics
        </div>
        <button type="button" onClick={onToggle}
                 className="text-[11px] text-muted-foreground hover:text-foreground"
                 data-testid="items-analytics-toggle">
          {expanded ? "Collapse" : "Expand"}
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 border-b border-border" data-testid="items-analytics-kpis">
        <KpiCell label="Total items" value={analytics.total_items || 0} testid="an-total-items" />
        <KpiCell label="Catalog value" value={fmtCurrency(analytics.total_value, currency)} testid="an-total-value" />
        <KpiCell label="Avg rate" value={fmtCurrency(analytics.avg_rate, currency)} testid="an-avg-rate" />
        <KpiCell label="Missing HSN" value={analytics.missing_hsn_count || 0}
                  tone={analytics.missing_hsn_count > 0 ? "warn" : "ok"}
                  hint={analytics.missing_hsn_count > 0 ? "Data-quality nudge — set HSN/SAC" : "All items tagged"}
                  testid="an-missing-hsn" />
      </div>
      {expanded && (
        <div className="p-3">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-2">
            By category
            {analytics.last_updated && (
              <span className="text-[10px] normal-case text-muted-foreground/70">
                · Last catalog update {fmtRel(analytics.last_updated)}
              </span>
            )}
          </div>
          {(!analytics.categories || analytics.categories.length === 0) ? (
            <div className="text-xs text-muted-foreground italic">
              Add categories via <b>Categories</b> to unlock per-bucket rollups.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {analytics.categories.map((c) => (
                <button key={c.name} type="button"
                         onClick={() => onCategoryClick(c.name)}
                         className={`text-left border p-2.5 hover:bg-muted/40 transition-colors ${activeCategory === c.name ? "border-primary bg-primary/5" : "border-border"}`}
                         data-testid={`an-cat-${c.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}>
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-2.5 h-2.5 border border-border"
                           style={{ background: c.color || "#94a3b8" }} />
                    <div className="text-xs font-semibold flex-1 min-w-0 truncate">{c.name}</div>
                    <div className="text-[10px] text-muted-foreground tabular-nums">{c.count}</div>
                  </div>
                  <div className="mt-1.5 grid grid-cols-3 gap-1 text-[10px] tabular-nums">
                    <div>
                      <div className="text-muted-foreground uppercase tracking-wide">Value</div>
                      <div className="font-mono">{fmtCurrency(c.total_value, currency)}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground uppercase tracking-wide">Avg rate</div>
                      <div className="font-mono">{fmtCurrency(c.avg_rate, currency)}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground uppercase tracking-wide">Missing HSN</div>
                      <div className={c.missing_hsn_count > 0 ? "font-mono text-amber-700" : "font-mono text-muted-foreground"}>
                        {c.missing_hsn_count || 0}
                      </div>
                    </div>
                  </div>
                  <div className="text-[9px] text-muted-foreground/80 mt-1">
                    Updated {fmtRel(c.last_updated)}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function KpiCell({ label, value, tone, hint, testid }) {
  const toneClass = tone === "warn" ? "text-amber-700"
                    : tone === "ok" ? "text-emerald-700" : "";
  return (
    <div className="p-3 border-r border-border last:border-r-0" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`text-xl font-bold tabular-nums ${toneClass}`}>{value}</div>
      {hint && <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}


/* ────── Spec sheet dialog ────── */
// Feb 2026 R293zv · Wraps AttachmentsPanel against the item-scoped
// backend (`/items/{iid}/attachments`). Picking this item from any
// Quotation / SO / Invoice composer copies these specs into that doc.
function ItemSpecsDialog({ item, onClose }) {
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="item-specs-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Paperclip className="w-4 h-4" />
            Spec sheets · {item.name}
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2 mb-3">
          Attachments on a catalog item ride along with the item every time it's picked into a quotation, sales order or invoice.
        </p>
        <AttachmentsPanel
          basePath={`/items/${item.id}/attachments`}
          initial={item.spec_attachments || []}
        />
      </DialogContent>
    </Dialog>
  );
}
